import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { meService, TenantAccountPayload } from '../services/api';
import './GuestStay.css';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDate(v: string | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const GuestStay: React.FC = () => {
  const [account, setAccount] = useState<TenantAccountPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [payAmount, setPayAmount] = useState('');
  const [paying, setPaying] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);

  const today = toYmd(new Date());
  const tomorrow = toYmd(new Date(Date.now() + 86400000));
  const [checkInDate, setCheckInDate] = useState(today);
  const [checkOutDate, setCheckOutDate] = useState(tomorrow);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await meService.getAccount();
      setAccount(data);
      const stay = data.guestStay;
      if (stay?.balanceDue != null && stay.balanceDue > 0) {
        setPayAmount(String(stay.balanceDue));
      }
      if (stay?.checkInDate) setCheckInDate(String(stay.checkInDate).slice(0, 10));
      if (stay?.checkOutDate) setCheckOutDate(String(stay.checkOutDate).slice(0, 10));
    } catch {
      toast.error('Failed to load stay details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stay = account?.guestStay;

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(payAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    try {
      setPaying(true);
      const res = await meService.payGuestStay(amount);
      setAccount(res);
      toast.success(res.message || 'Payment recorded');
      if (res.guestStay?.balanceDue != null && res.guestStay.balanceDue > 0) {
        setPayAmount(String(res.guestStay.balanceDue));
      } else {
        setPayAmount('');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const handleReCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkInDate || !checkOutDate) {
      toast.error('Select check-in and check-out dates');
      return;
    }
    try {
      setCheckingIn(true);
      const res = await meService.reCheckInGuest({
        checkInDate,
        checkOutDate,
        dailyRate: stay?.dailyRate,
      });
      setAccount(res);
      toast.success(res.message || 'Check-in recorded');
      if (res.guestStay?.balanceDue != null && res.guestStay.balanceDue > 0) {
        setPayAmount(String(res.guestStay.balanceDue));
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not check in');
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return <div className="guest-stay guest-stay--loading">Loading…</div>;
  }

  const balance = stay?.balanceDue ?? 0;
  const hasActiveStay = stay && String(stay.status || '').toUpperCase() === 'ACTIVE';

  return (
    <div className="guest-stay">
      <header className="guest-stay__head">
        <h1>Pay &amp; stay</h1>
        <p className="guest-stay__sub">Manage your current stay, payments, and re-check-in.</p>
      </header>

      {stay ? (
        <section className="guest-stay-card guest-stay-card--current">
          <h2>Current stay</h2>
          <div className="guest-stay-grid">
            <div>
              <span className="guest-stay-label">Check-in</span>
              <strong>{formatDate(stay.checkInDate)}</strong>
            </div>
            <div>
              <span className="guest-stay-label">Check-out</span>
              <strong>{formatDate(stay.checkOutDate)}</strong>
            </div>
            <div>
              <span className="guest-stay-label">Location</span>
              <strong>{stay.location?.display || '—'}</strong>
            </div>
            <div>
              <span className="guest-stay-label">Days × rate</span>
              <strong>
                {stay.days} × ₹{Number(stay.dailyRate || 0).toLocaleString('en-IN')}
              </strong>
            </div>
            <div>
              <span className="guest-stay-label">Total</span>
              <strong>₹{Number(stay.totalDue || 0).toLocaleString('en-IN')}</strong>
            </div>
            <div>
              <span className="guest-stay-label">Paid</span>
              <strong>₹{Number(stay.amountPaid || 0).toLocaleString('en-IN')}</strong>
            </div>
          </div>
          <p className={`guest-stay-balance${balance > 0 ? ' guest-stay-balance--due' : ''}`}>
            Balance due: <strong>₹{balance.toLocaleString('en-IN')}</strong>
          </p>
        </section>
      ) : (
        <section className="guest-stay-card guest-stay-card--empty">
          <p>No active stay on file. Use re-check-in below to start a new stay (subject to hostel approval).</p>
        </section>
      )}

      {hasActiveStay && balance > 0 ? (
        <section className="guest-stay-card">
          <h2>Pay balance</h2>
          <form className="guest-stay-form" onSubmit={(e) => void handlePay(e)}>
            <label>
              Amount (₹)
              <input
                type="number"
                min="1"
                step="1"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="guest-stay-btn guest-stay-btn--primary" disabled={paying}>
              {paying ? 'Processing…' : 'Record payment'}
            </button>
          </form>
        </section>
      ) : hasActiveStay ? (
        <section className="guest-stay-card guest-stay-card--ok">
          <p>No balance due on your current stay.</p>
        </section>
      ) : null}

      <section className="guest-stay-card">
        <h2>Re-check-in</h2>
        <p className="guest-stay-hint">
          Request a new stay period. Your previous active stay will be marked completed.
        </p>
        <form className="guest-stay-form" onSubmit={(e) => void handleReCheckIn(e)}>
          <label>
            Check-in
            <input type="date" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} required />
          </label>
          <label>
            Check-out
            <input type="date" value={checkOutDate} onChange={(e) => setCheckOutDate(e.target.value)} required />
          </label>
          <button type="submit" className="guest-stay-btn guest-stay-btn--secondary" disabled={checkingIn}>
            {checkingIn ? 'Saving…' : 'Check in again'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default GuestStay;
